/**
 * Unified Gemini Client
 *
 * Single entry point for all Gemini API calls through the server proxy.
 * Replaces the duplicated callProxy/callGeminiProxy logic that was spread
 * across agentService.ts and mmmReportService.ts.
 *
 * Methods:
 *   chat()             — plain text generation
 *   generateStructured() — JSON output with responseSchema
 *   streamChat()       — SSE streaming, yields text chunks
 *
 * Issue: YDMA-178 §2.1
 */

import { getSupabaseClient } from '@/config/supabase';
import { getActiveWorkspaceId } from '@/services/activeWorkspace';
import { CodedError } from './codedError';

/**
 * A failure that must NOT be retried because the call already cost money.
 *
 * The proxy answers 200 once Gemini has answered — at which point the tokens
 * are spent and the workspace has been metered and charged. Anything that goes
 * wrong after that (an empty candidate list, an unparseable payload) is a
 * failed RESULT, not a failed request: retrying it buys a second charge for
 * output the user still never sees.
 */
class NonRetryableGeminiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NonRetryableGeminiError';
  }
}

/**
 * The caller's session, for the proxy routes' gate (api/_lib/authorizeGeminiCall).
 *
 * Neither call site sent one until 2026-09-01, which is why both routes were
 * reachable anonymously. Throwing here rather than sending the request anyway
 * matters for the message the user gets: an anonymous POST would come back 401
 * and surface as "the AI is down" instead of "you are signed out".
 */
const SESSION_EXPIRED_MESSAGE =
  'Your session has expired — please sign in again to use the AI assistant.';

const authHeaders = async (): Promise<Record<string, string>> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new CodedError('sessionExpired', SESSION_EXPIRED_MESSAGE);
  }
  const workspaceId = getActiveWorkspaceId();
  // A header, deliberately: the proxy forwards the BODY to Google, so anything
  // added there would leave our systems.
  return {
    Authorization: `Bearer ${token}`,
    ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
  };
};

/**
 * The proxy's 401 is a sign-in problem, and it must not read as an AI outage.
 * The route replies with the bare word "Unauthorized" — correct for an API,
 * useless in a chat bubble — so it is translated here rather than shown.
 */
const proxyErrorMessage = (status: number, serverMessage: string): string =>
  status === 401 ? SESSION_EXPIRED_MESSAGE : serverMessage;

// ============================================================
// CONSTANTS
// ============================================================

const PROXY_ENDPOINT = '/api/gemini-chat';
const STREAM_PROXY_ENDPOINT = '/api/gemini-chat-stream';

const DEFAULT_MODEL = 'gemini-2.5-flash';
const DEFAULT_API_VERSION = 'v1beta';
const DEFAULT_TIMEOUT_MS = 30_000;
const STREAM_TIMEOUT_MS = 90_000; // streaming allows more time (thinking models)
const MAX_RETRIES = 1;

// ============================================================
// TYPES
// ============================================================

export interface GeminiContent {
  role: 'user' | 'model';
  parts: Array<{ text: string }>;
}

export interface GenerationConfig {
  temperature?: number;
  topK?: number;
  topP?: number;
  maxOutputTokens?: number;
  responseMimeType?: string;
  responseSchema?: Record<string, unknown>;
  /**
   * Gemini 2.5 Flash "thinking" budget control.
   * `thinkingBudget: 0` disables thinking entirely — frees the full
   * maxOutputTokens for actual output. Use for structured generation
   * (reports, templates) where reasoning depth isn't critical.
   * Default (omit) lets the model decide; can consume 1-3k of the budget.
   */
  thinkingConfig?: { thinkingBudget?: number };
}

export interface ChatOptions {
  model?: string;
  apiVersion?: string;
  contents: GeminiContent[];
  generationConfig?: GenerationConfig;
  /** Abort signal for cancellation */
  signal?: AbortSignal;
  /** Per-request timeout override (ms) */
  timeoutMs?: number;
}

export interface StructuredOptions extends ChatOptions {
  responseSchema: Record<string, unknown>;
}

export interface StreamCallbacks {
  onChunk: (text: string) => void;
  signal?: AbortSignal;
}

// ============================================================
// GEMINI CLIENT
// ============================================================

/**
 * Unified client for Gemini API calls via the server proxy.
 *
 * Usage:
 * ```ts
 * const client = GeminiClient.getInstance();
 *
 * // Plain text
 * const text = await client.chat({ contents: [...] });
 *
 * // Structured JSON
 * const json = await client.generateStructured({
 *   contents: [...],
 *   responseSchema: MY_SCHEMA,
 * });
 *
 * // Streaming
 * await client.streamChat(
 *   { contents: [...] },
 *   { onChunk: (chunk) => updateUI(chunk) }
 * );
 * ```
 */
export class GeminiClient {
  private static _instance: GeminiClient | null = null;

  static getInstance(): GeminiClient {
    if (!GeminiClient._instance) GeminiClient._instance = new GeminiClient();
    return GeminiClient._instance;
  }

  // ============================================================
  // chat() — plain text generation
  // ============================================================

  /**
   * Sends a generateContent request and returns the raw text response.
   * Includes retry on 5xx and timeout handling.
   */
  async chat(options: ChatOptions): Promise<string> {
    const payload = this.buildPayload(options);
    return this.callProxy(payload, options.signal, options.timeoutMs);
  }

  // ============================================================
  // generateStructured() — JSON output with schema
  // ============================================================

  /**
   * Like chat(), but enforces JSON output via responseMimeType + responseSchema.
   * Returns the raw JSON string (caller parses).
   */
  async generateStructured(options: StructuredOptions): Promise<string> {
    const payload = this.buildPayload({
      ...options,
      generationConfig: {
        ...options.generationConfig,
        responseMimeType: 'application/json',
        responseSchema: options.responseSchema,
      },
    });

    return this.callProxy(payload, options.signal, options.timeoutMs);
  }

  // ============================================================
  // streamChat() — SSE streaming
  // ============================================================

  /**
   * Streams text chunks from the Gemini API via SSE.
   *
   * Calls `onChunk(text)` for each incremental text piece received.
   * Returns the full accumulated text when the stream completes.
   *
   * The streaming endpoint uses Gemini's `streamGenerateContent?alt=sse`
   * and the proxy at `/api/gemini-chat-stream` passes SSE events through.
   */
  async streamChat(
    options: ChatOptions,
    callbacks: StreamCallbacks
  ): Promise<string> {
    const payload = this.buildPayload(options);
    const controller = new AbortController();

    // Link external signal if provided
    if (callbacks.signal) {
      callbacks.signal.addEventListener('abort', () => controller.abort());
    }

    const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs ?? STREAM_TIMEOUT_MS);

    let accumulated = '';

    try {
      const response = await fetch(STREAM_PROXY_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(await authHeaders()),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let message = `Gemini stream proxy ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed?.error) message = proxyErrorMessage(response.status, parsed.error);
        } catch {
          // keep default
        }
        throw new Error(message);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Streaming not supported in this environment');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events.
        // Gemini API uses \r\n\r\n between events (standard SSE over HTTP),
        // but some proxies may normalize to \n\n — handle both.
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() ?? ''; // keep incomplete chunk

        for (const event of events) {
          const chunk = this.parseSSEEvent(event);
          if (chunk) {
            accumulated += chunk;
            callbacks.onChunk(chunk);
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        const chunk = this.parseSSEEvent(buffer);
        if (chunk) {
          accumulated += chunk;
          callbacks.onChunk(chunk);
        }
      }

      return accumulated;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        if (callbacks.signal?.aborted) {
          throw new Error('Stream cancelled by user');
        }
        throw new Error('Stream timed out — try again or use a guided question');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // ============================================================
  // INTERNAL: build payload
  // ============================================================

  private buildPayload(options: ChatOptions): unknown {
    return {
      model: options.model ?? DEFAULT_MODEL,
      apiVersion: options.apiVersion ?? DEFAULT_API_VERSION,
      contents: options.contents,
      generationConfig: options.generationConfig ?? {},
    };
  }

  // ============================================================
  // INTERNAL: call proxy (non-streaming, with retry)
  // ============================================================

  private async callProxy(
    payload: unknown,
    externalSignal?: AbortSignal,
    timeoutMs?: number
  ): Promise<string> {
    let lastError: Error | null = null;

    // Resolved ONCE, before the loop and before any timer is armed. Inside the
    // try it looked harmless, but a signed-out caller threw past clearTimeout
    // into the catch, which classifies anything non-abort as retryable — so a
    // missing session was retried (it can never succeed) and left an abort timer
    // armed on every attempt. streamChat has a finally; this path did not.
    const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        timeoutMs ?? DEFAULT_TIMEOUT_MS
      );

      try {
        // Link external signal
        if (externalSignal) {
          externalSignal.addEventListener('abort', () => controller.abort());
        }

        const response = await fetch(PROXY_ENDPOINT, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        const raw = await response.text();
        let data: unknown = null;
        try {
          data = raw ? JSON.parse(raw) : null;
        } catch {
          // non-JSON body
        }

        if (!response.ok) {
          const message = proxyErrorMessage(
            response.status,
            data && typeof data === 'object' && 'error' in data &&
              typeof (data as { error: unknown }).error === 'string'
              ? (data as { error: string }).error
              : `Gemini proxy ${response.status}`,
          );

          // Retry on 5xx but not on 4xx
          if (response.status >= 500 && attempt < MAX_RETRIES) {
            lastError = new Error(message);
            continue;
          }
          throw new Error(message);
        }

        const candidate =
          (data as { candidates?: Array<{ content?: { parts?: Array<{ text?: string }> }; finishReason?: string }> } | null)
            ?.candidates?.[0];
        const text: string | undefined = candidate?.content?.parts?.[0]?.text;
        const finishReason = candidate?.finishReason;

        if (typeof text !== 'string') {
          // NOT retryable. The server already answered 200, which means the
          // upstream call succeeded, the tokens were spent, and the workspace
          // was metered and charged. Retrying bills a second time for output
          // the user never receives — so this is thrown past the retry loop.
          throw new NonRetryableGeminiError('Gemini proxy returned no text content');
        }

        // Surface silent truncation. When Gemini hits maxOutputTokens it returns
        // a partial response with finishReason='MAX_TOKENS' — caller previously
        // got back text that just stopped mid-sentence with no indication.
        // We log it (so devs see it in console) AND append a visible marker so
        // downstream renderers (and the user) can tell the report is incomplete.
        if (finishReason === 'MAX_TOKENS') {
          // eslint-disable-next-line no-console
          console.warn(
            '[geminiClient] Response truncated by maxOutputTokens. ' +
            'Bump maxOutputTokens or disable thinking via thinkingConfig.thinkingBudget=0.'
          );
          return text + '\n\n<!-- [TRUNCATED: Gemini hit maxOutputTokens — response is incomplete] -->';
        }
        return text;
      } catch (err) {
        // Don't retry on abort (timeout) — just rethrow
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw new Error('Request timed out after 30s — try again or use a guided question');
        }
        // A billed response is never retried: the cost is already incurred.
        if (err instanceof NonRetryableGeminiError) {
          throw err;
        }
        // Retry on network errors
        if (attempt < MAX_RETRIES) {
          lastError = err instanceof Error ? err : new Error(String(err));
          continue;
        }
        throw err;
      } finally {
        // Every exit path, not just the happy one: a throw used to leave the
        // abort timer armed for the full timeout after the call had rejected.
        clearTimeout(timeoutId);
      }
    }

    throw lastError ?? new Error('callProxy exhausted retries');
  }

  // ============================================================
  // INTERNAL: parse SSE event
  // ============================================================

  /**
   * Parses a single SSE event from Gemini's streamGenerateContent response.
   * Extracts text from all `data:` lines and concatenates them.
   *
   * Normally each SSE event has exactly one data line, but if events arrive
   * concatenated (e.g. due to proxy buffering), we collect ALL text chunks
   * instead of silently dropping everything after the first.
   */
  private parseSSEEvent(event: string): string | null {
    const lines = event.split(/\r?\n/);
    let result = '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr || jsonStr === '[DONE]') continue;

      try {
        const parsed = JSON.parse(jsonStr) as {
          candidates?: Array<{
            content?: {
              parts?: Array<{ text?: string }>;
            };
          }>;
        };
        const text = parsed.candidates?.[0]?.content?.parts?.[0]?.text;
        if (typeof text === 'string' && text.length > 0) {
          result += text;
        }
      } catch {
        // Partial JSON — skip incomplete chunks
      }
    }
    return result.length > 0 ? result : null;
  }
}

// ============================================================
// SINGLETON EXPORT
// ============================================================

export const geminiClient = GeminiClient.getInstance();

export default geminiClient;
